import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { api } from '../api';
import {
  ApiError,
  type HuntAoEncher,
  type HuntAoVer,
  type HuntArea,
  type HuntBoard,
  type HuntCarry,
  type HuntFora,
  type HuntLoot,
  type HuntMob,
  type HuntOrder,
  type HuntRegras,
  type HuntView
} from '../api/types';
import { PILE_PRATA } from '../assets/items';
import { IconeItem } from './IconeItem';
import { GumpWindow } from './GumpWindow';
import { FaceMob, ImgAparada, RetratoAnimado } from './RetratoMob';
import { IcAr, IcCofre, IcCoroa, IcDano, IcMonstro, IcParalisa, IcPeso, IcRelogio, IcRm, IcVeneno, IcVida } from './icones/IconesV3';
import { numero, t } from '../i18n/idioma';
import { nomeDoInimigo, nomeDoItem } from '../i18n/nomes';
import { tempoDesde } from './hud/useTempoCacada';
import { useCarrossel } from './useCarrossel';

/**
 * O modo muda no servidor sem o cliente pedir — basta o jogador apertar uma
 * tecla (11.6). Sem esta recarga o painel mentiria até o próximo clique. É
 * também o que traz a contagem da caçada (abates, prata, saque).
 */
const RECARGA_MS = 5_000;
/** Na pausa da mochila cheia as fases andam rápido: recarrega mais. */
const RECARGA_PAUSA_MS = 1_500;

/** Sem o board (servidor antigo), os números do protótipo. */
const REGRAS_PADRAO: HuntRegras = {
  recallS: 4.5, guardarS: 3, vendaS: 2, recompraMin: 15, raroPadrao: 10, raroMin: 0, raroMax: 50, banco: ''
};

/** "3 h", "2 h 15 min", "15 min", "40 s". */
function duracao(seg: number) {
  const s = Math.max(0, Math.round(seg));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? (m ? `${h} h ${m} min` : `${h} h`) : `${m} min`;
}

/** Segundos com uma casa (vírgula no pt): "12 s", "8,6 s". */
const segundos = (s: number) => `${numero(Math.round(s * 10) / 10)} s`;

const AO_VER: Readonly<Record<HuntAoVer, { nome: string; dica: string; img: string }>> = {
  batalhar: { nome: 'cacar.aoVer.batalhar', dica: 'ch.aoVer.batalharDica', img: '/sprites/itens/weapons/large-battle-axe.png' },
  correr: { nome: 'cacar.aoVer.correr', dica: 'ch.aoVer.correrDica', img: '/sprites/itens/skills/recall.png' }
};

const CAVEIRAS: Readonly<Record<string, number>> = { nenhum: 0, baixo: 1, medio: 2, alto: 3 };

interface Props {
  dead: boolean;
  fadiga?: number;
  fadigaMax?: number;
  /** A etiqueta da barra também é escrita daqui: cancelar e voltar ao
   *  automático não passam pelo aviso do servidor. */
  onModo(manual: boolean): void;
  onOrdem?(ordem: HuntOrder | null): void;
  /** A caçada escolhida em Viajar › Caçadas: abre já nela. */
  inicial?: string | null;
  /** "Ver VIP" do aviso de Vender (techspec-cacada-venda): abre a loja na aba VIP. */
  onVerVip?(): void;
  onClose(): void;
  /** Em caçada a janela minimiza para a pílula do idle (techspec-hud-v3 §18). */
  onMinimize?(): void;
}

const aparenciaDe = (m: HuntMob) => m.resumo.aparencia || m.defId;
const nomeMob = (m: HuntMob) => nomeDoInimigo(m.defId, m.resumo.nome);
/** O card da lista mostra no máximo estes rostos (uma linha); o resto vira "+N" (a Ice Dungeon tem nove). */
const RAIL_MAX_MOBS = 2;
/** Até 4 inimigos a ficha é a grade de sempre (duas colunas); acima disso vira carrossel de duas linhas. */
const MOBS_SEM_CARROSSEL = 4;
/** A maior chance entre os mobs que deixam cair. */
const chanceDe = (l: HuntLoot) => Math.max(l.chance, ...(l.porMob ?? []).map(p => p.chance));
/** Do mais fácil ao mais difícil: chance maior primeiro (a prata, sempre, abre a grade). */
const ordenar = (loot: readonly HuntLoot[]) =>
  [...loot].sort((a, b) => (b.defId === 'silver' ? 1 : 0) - (a.defId === 'silver' ? 1 : 0) || chanceDe(b) - chanceDe(a));

/**
 * Caçadas (techspec-hud-v3 §09): a lista com nível; a ficha com os inimigos em ícone, o saque em grade
 * que liga e desliga, "se vier um jogador" em dois botões e o rodapé com o que vai coletar. Em caçada:
 * o tempo, os abates por mob, a prata e o saque desta caçada, a mochila e Voltar à cidade.
 */
export function HuntGump({ dead, onModo, onOrdem, inicial, onVerVip, onClose, onMinimize }: Props) {
  const [board, setBoard] = useState<HuntBoard | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(inicial ?? null);
  useEffect(() => { if (inicial) setSel(inicial); }, [inicial]);
  const [aoVer, setAoVer] = useState<HuntAoVer>('batalhar');
  // Área PvP (padrão) ou protegida (techspec-cacada-protegida). Sem área protegida na caçada, sempre PvP.
  const [area, setArea] = useState<HuntArea>('pvp');
  const [filtro, setFiltro] = useState<readonly string[]>([]);
  const [aoEncher, setAoEncher] = useState<HuntAoEncher>('cofre');
  const [raro, setRaro] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const hunt: HuntView | null = board?.cacadas.find(h => h.id === sel) ?? board?.cacadas[0] ?? null;

  const carregar = useCallback(async () => {
    try {
      const next = await api.getHunts();
      setBoard(next);
      onOrdem?.(next.ordem);
      onModo(next.ordem?.modo === 'manual');
      setErro(null);
      setSel(cur => cur && next.cacadas.some(h => h.id === cur) ? cur : next.cacadas[0]?.id ?? null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : t('comum.erroGenerico'));
    }
  }, [onModo, onOrdem]);

  useEffect(() => { void carregar(); }, [carregar]);

  const emPausa = !!board?.ordem?.pausa;
  useEffect(() => {
    if (!board?.ordem) return;
    const id = window.setInterval(() => void carregar(), emPausa ? RECARGA_PAUSA_MS : RECARGA_MS);
    return () => window.clearInterval(id);
  }, [board?.ordem, emPausa, carregar]);

  useEffect(() => {
    if (!hunt) return;
    setFiltro(hunt.filtro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hunt?.id]);

  const toggleLoot = (defId: string) =>
    setFiltro(cur => cur.includes(defId) ? cur.filter(x => x !== defId) : [...cur, defId]);

  const chamar = async (f: () => Promise<HuntBoard>, modo?: boolean) => {
    setBusy(true);
    try {
      const next = await f();
      setBoard(next);
      onOrdem?.(next.ordem);
      onModo(modo ?? next.ordem?.modo === 'manual');
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : t('comum.erroGenerico'));
    } finally {
      setBusy(false);
    }
  };

  const fantasma = dead || !!board?.morto;
  const ordem = board?.ordem ?? null;
  const huntDaOrdem = ordem ? board?.cacadas.find(h => h.id === ordem.huntId) ?? null : null;
  const vip = !!board?.vip;
  const regras = board?.regras ?? REGRAS_PADRAO;
  const raroAtual = raro ?? regras.raroPadrao;
  // Sem VIP, Vender não vale: a escolha fica em Guardar (o servidor também recusa).
  const aoEncherEfetivo: HuntAoEncher = vip ? aoEncher : 'cofre';
  const fora = board?.fora ?? null;
  const cartaoFora = fora && (
    <CartaoFora fora={fora} hunt={board?.cacadas.find(h => h.id === fora.huntId) ?? null} busy={busy}
      onOk={() => void chamar(() => api.foraVistoHunt())} />
  );

  if (ordem) {
    return (
      <GumpWindow title={t('cacar.tituloLive')} anchor={{ left: 72, top: 64 }}
        className={`win--hunt ch-live${ordem.pausa ? ' is-pausa' : ''}`} posicao="cacar" onClose={onClose} onMinimize={onMinimize}>
        {cartaoFora}
        <EmCacada
          ordem={ordem}
          hunt={huntDaOrdem}
          voce={board?.voce ?? null}
          fantasma={fantasma}
          busy={busy}
          vip={vip}
          regras={regras}
          onAuto={() => void chamar(() => api.autoHunt(), false)}
          onVoltar={() => void chamar(() => api.cancelHunt(), false)}
          onRegra={ao => void chamar(() => api.regraHunt({ aoEncher: ao }))}
          onVerVip={onVerVip}
        />
        {erro && <p className="ch-aviso" role="alert">{erro}</p>}
      </GumpWindow>
    );
  }

  return (
    <GumpWindow title={t('cacar.titulo')} anchor={{ left: 72, top: 64 }} className="win--hunt ch" posicao="cacar" onClose={onClose}>
      <div className="ch-grid">
        <nav className="ch-rail" aria-label={t('cacar.titulo')}>
          {(board?.cacadas ?? []).map(c => (
            <button key={c.id} type="button" className="ch-card" aria-pressed={hunt?.id === c.id} onClick={() => setSel(c.id)}>
              <span className="ch-card__t"><b>{t(`cacada.${c.id}.nome`)}</b><span className="ch-nv">{t('cacar.nv', { n: c.nivel })}</span></span>
              <span className="ch-card__m">
                {c.inimigos.slice(0, RAIL_MAX_MOBS).map(m => (
                  <Fragment key={m.defId}>
                    <FaceMob aparencia={aparenciaDe(m)} px={30} titulo={nomeMob(m)} /><small>×{m.quantidade}</small>
                  </Fragment>
                ))}
                {c.inimigos.length > RAIL_MAX_MOBS && (
                  <small
                    className="ch-card__mais"
                    title={c.inimigos.slice(RAIL_MAX_MOBS).map(m => `${nomeMob(m)} ×${m.quantidade}`).join(', ')}
                  >
                    {t('ch.maisMobs', { n: c.inimigos.length - RAIL_MAX_MOBS })}
                  </small>
                )}
              </span>
            </button>
          ))}
          <p className="ch-breve">{t('ch.emBreve')}</p>
        </nav>
        {hunt && (
          <Ficha
            hunt={hunt}
            filtro={filtro}
            aoVer={aoVer}
            area={hunt.protegida ? area : 'pvp'}
            onArea={setArea}
            fantasma={fantasma}
            busy={busy}
            vip={vip}
            regras={regras}
            aoEncher={aoEncherEfetivo}
            raro={raroAtual}
            topo={cartaoFora}
            onToggle={toggleLoot}
            onTodos={on => setFiltro(on ? hunt.loot.map(l => l.defId) : [])}
            onAoVer={setAoVer}
            onAoEncher={setAoEncher}
            onRaro={setRaro}
            onVerVip={onVerVip}
            onCacar={() => {
              if (!hunt || fantasma) return;
              void chamar(() => api.startHunt({
                huntId: hunt.id, filtro, modo: 'automatico', aoVerJogador: aoVer,
                aoEncher: aoEncherEfetivo, raroAbaixoDe: raroAtual,
                area: hunt.protegida ? area : 'pvp'
              }));
            }}
          />
        )}
      </div>
      {erro && <p className="ch-aviso" role="alert">{erro}</p>}
    </GumpWindow>
  );
}

/** O escudo da área protegida (techspec-cacada-protegida). */
function IcEscudo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z" />
      <path d="m8.5 12 2.5 2.5 4.5-5" fill="none" />
    </svg>
  );
}

/** A chance com a redução da área protegida (a prata cai sempre: a redução é na quantidade). */
function chanceNaArea(c: number, defId: string, p: HuntView['protegida'], area: HuntArea): number {
  if (area !== 'protegida' || !p || defId === 'silver') return c;
  const r = c < p.limiteRaro ? p.raros : p.itens;
  return Math.round(c * (100 - r) / 10) / 10;
}

function Ficha({
  hunt, filtro, aoVer, area, fantasma, busy, vip, regras, aoEncher, raro, topo,
  onToggle, onTodos, onAoVer, onArea, onAoEncher, onRaro, onVerVip, onCacar
}: {
  hunt: HuntView;
  filtro: readonly string[];
  aoVer: HuntAoVer;
  area: HuntArea;
  onArea(a: HuntArea): void;
  fantasma: boolean;
  busy: boolean;
  vip: boolean;
  regras: HuntRegras;
  aoEncher: HuntAoEncher;
  raro: number;
  topo?: ReactNode;
  onToggle(id: string): void;
  onTodos(on: boolean): void;
  onAoVer(v: HuntAoVer): void;
  onAoEncher(v: HuntAoEncher): void;
  onRaro(v: number): void;
  onVerVip?(): void;
  onCacar(): void;
}) {
  const [upsell, setUpsell] = useState(false);
  const prot = area === 'protegida' && !!hunt.protegida;
  const custo = hunt.protegida
    ? t('ch.area.custo', { xp: hunt.protegida.xp, saque: Math.max(hunt.protegida.prata, hunt.protegida.itens) })
    : '';
  const caveiras = CAVEIRAS[hunt.risco] ?? 0;
  const loot = ordenar(hunt.loot);
  const leva = loot.filter(l => filtro.includes(l.defId));
  const variosMobs = hunt.inimigos.length > 1;
  const mobPorId = new Map(hunt.inimigos.map(m => [m.defId, m]));
  const viagem = segundos(2 * regras.recallS + regras.guardarS);
  // O que cai com chance abaixo do slider vai para o cofre em vez de ser vendido.
  const raros = loot.filter(l => l.defId !== 'silver' && filtro.includes(l.defId) && chanceDe(l) < raro);
  const escolherVender = () => {
    if (!vip) { setUpsell(true); return; }
    onAoEncher('vender');
  };
  return (
    <div className="ch-ficha">
      {topo}
      <header className="ch-hx">
        <h3>{t(`cacada.${hunt.id}.nome`)}</h3>
        <span className="ch-nv">{t('cacar.nv', { n: hunt.nivel })}</span>
        <span className="ch-risco" title={t('ch.risco', { risco: t(`cacar.risco.${hunt.risco}`) })} aria-label={t('ch.risco', { risco: t(`cacar.risco.${hunt.risco}`) })}>
          {[1, 2, 3].map(n => <i key={n} className={n <= caveiras ? 'is-on' : ''} />)}
        </span>
      </header>

      {hunt.protegida && (
        <>
          <h4 className="ch-sec">{t('ch.area')}</h4>
          <div className="ch-pvp ch-area">
            <button type="button" className="ch-op ch-area__op is-pvp" aria-pressed={!prot} onClick={() => onArea('pvp')}>
              <span className="ch-area__ic" aria-hidden="true"><IcMonstro /></span>
              <b>{t('ch.area.pvp')}</b><small>{t('ch.area.pvpDica')}</small>
            </button>
            <button type="button" className="ch-op ch-area__op is-prot" aria-pressed={prot} onClick={() => onArea('protegida')}>
              <span className="ch-area__ic" aria-hidden="true"><IcEscudo /></span>
              <b>{t('ch.area.protegida')}</b><small>{t('ch.area.protegidaDica')} <em>{custo}</em></small>
            </button>
          </div>
        </>
      )}

      <MobsDaFicha key={hunt.id} inimigos={hunt.inimigos} />

      <h4 className="ch-sec">
        {t('ch.saque')}
        <span className="ch-sec__dir">
          <button type="button" className="ch-mini" onClick={() => onTodos(true)}>{t('ch.tudo')}</button>
          <button type="button" className="ch-mini" onClick={() => onTodos(false)}>{t('ch.nada')}</button>
        </span>
      </h4>
      <div className="ch-loot">
        {loot.map(l => {
          const on = filtro.includes(l.defId);
          const c = chanceNaArea(chanceDe(l), l.defId, hunt.protegida, prot ? 'protegida' : 'pvp');
          const de = (l.porMob ?? []).map(p => mobPorId.get(p.mobId)).filter((m): m is HuntMob => !!m);
          const dica = `${nomeDoItem(l.defId)} · ${c >= 100 ? '100%' : `${c}%`}${de.length ? ` · ${de.map(nomeMob).join(', ')}` : ''}`;
          return (
            <button
              key={l.defId}
              type="button"
              className={`ch-it${on ? '' : ' is-off'}${c < 10 ? ' is-raro' : ''}`}
              aria-pressed={on}
              aria-label={dica}
              title={dica}
              onClick={() => onToggle(l.defId)}
            >
              {variosMobs && de.length > 0 && (
                <span className="ch-it__de">{de.map(m => <FaceMob key={m.defId} aparencia={aparenciaDe(m)} px={14} />)}</span>
              )}
              <IconeItem className="ch-it__ic" id={l.defId} />
              <span className="ch-it__ok">✓</span>
              <span className="ch-it__c">{c >= 100 ? '100%' : `${c}%`}</span>
            </button>
          );
        })}
      </div>

      <h4 className="ch-sec">{t('cv.mochilaCheia')}</h4>
      <div className="ch-pvp cv-cheia">
        <button type="button" className="ch-op" aria-pressed={aoEncher === 'cofre'} onClick={() => { setUpsell(false); onAoEncher('cofre'); }}>
          <IcCofre /><b>{t('cv.cofre')}</b><small>{t('cv.cofreDica', { s: viagem })}</small>
        </button>
        <button type="button" className={`ch-op${vip ? '' : ' is-trava'}`} aria-pressed={aoEncher === 'vender'}
          title={vip ? undefined : t('cv.soVip')} onClick={escolherVender}>
          <ImgAparada src={PILE_PRATA} /><b>{t('cv.vender')}</b><small>{t('cv.venderDica')}</small>
          <span className="cv-coroa"><IcCoroa />VIP</span>
        </button>
      </div>
      {upsell && !vip && (
        <div className="cv-vip" role="status">
          <span><b>{t('cv.upsellTitulo')}</b> {t('cv.upsellTexto')}</span>
          <span>{t('cv.upsellSemVip')}</span>
          {onVerVip && <button type="button" className="btn btn--tiny btn--primary" onClick={onVerVip}>{t('cv.verVip')}</button>}
        </div>
      )}
      {aoEncher === 'vender' && (
        <div className="cv-raros">
          <label htmlFor="cv-raro">{t('cv.raros')} <b>{raro}%</b></label>
          <input id="cv-raro" type="range" min={regras.raroMin} max={regras.raroMax} step={1} value={raro}
            aria-label={t('cv.rarosDica')} onChange={e => onRaro(Number(e.target.value))} />
          <span className="cv-raros__ic">
            {raros.length
              ? raros.map(l => <IconeItem key={l.defId} id={l.defId} title={`${nomeDoItem(l.defId)} · ${chanceDe(l)}%`} />)
              : <em>{t('cv.vendeTudo')}</em>}
          </span>
        </div>
      )}

      {prot ? (
        <div className="ch-area__nota" role="note">
          <IcEscudo className="ch-area__esc" />
          <span><b>{t('ch.area.notaTitulo', { cacada: t(`cacada.${hunt.id}.nome`) })}</b> {t('ch.area.nota')}</span>
        </div>
      ) : (
        <>
          <h4 className="ch-sec">{t('ch.seVierJogador')}</h4>
          <div className="ch-pvp">
            {(Object.keys(AO_VER) as HuntAoVer[]).map(k => (
              <button key={k} type="button" className="ch-op" aria-pressed={aoVer === k} onClick={() => onAoVer(k)}>
                <ImgAparada src={AO_VER[k].img} /><b>{t(AO_VER[k].nome)}</b><small>{t(AO_VER[k].dica)}</small>
              </button>
            ))}
          </div>
        </>
      )}
      {fantasma && <p className="ch-aviso">{t('ch.fantasma')}</p>}

      <footer className="ch-pe">
        <div className="ch-pe__leva" title={t('ch.vaiColetar')}>
          {leva.slice(0, 7).map(l => <IconeItem key={l.defId} id={l.defId} />)}
          {leva.length > 7 && <span className="ch-pe__mais">+{leva.length - 7}</span>}
          {leva.length === 0 && <span className="ch-pe__mais">{t('ch.nadaColeta')}</span>}
          <span className="ch-pe__ao" title={t('cv.regraDica', { regra: t(aoEncher === 'vender' ? 'cv.vender' : 'cv.cofre') })}>
            {aoEncher === 'vender' ? <ImgAparada src={PILE_PRATA} /> : <IcCofre />}
          </span>
          {prot
            ? <span className="ch-pe__ao ch-area__pe" title={t('ch.area.protegida')}><IcEscudo /></span>
            : <span className="ch-pe__ao" title={t('ch.aoVerDica', { modo: t(AO_VER[aoVer].nome) })}><ImgAparada src={AO_VER[aoVer].img} /></span>}
        </div>
        <button type="button" className="btn btn--primary hunt-cta" disabled={fantasma || busy} onClick={onCacar}>
          {prot ? t('ch.cacarProtegido') : t('mundo.cacar')}
        </button>
      </footer>
    </div>
  );
}

/** O texto de cada fase da pausa (o código vem do servidor). */
function textoDaFase(codigo: string, banco: string, cacada: string) {
  switch (codigo) {
    case 'recall-ida': return banco ? t('cv.faseIda', { cidade: banco }) : t('cv.faseIdaSem');
    case 'guardando': return t('cv.faseGuardando');
    case 'recall-volta': return t('cv.faseVolta', { cacada });
    case 'vendendo': return t('cv.faseVendendo');
    default: return t('cv.fasePendente');
  }
}

function EmCacada({ ordem, hunt, voce, fantasma, busy, vip, regras, onAuto, onVoltar, onRegra, onVerVip }: {
  ordem: HuntOrder;
  hunt: HuntView | null;
  voce: HuntCarry | null;
  fantasma: boolean;
  busy: boolean;
  vip: boolean;
  regras: HuntRegras;
  onAuto(): void;
  onVoltar(): void;
  onRegra(ao: HuntAoEncher): void;
  onVerVip?(): void;
}) {
  const [aba, setAba] = useState(false);
  const [explica, setExplica] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());
  const pausa = ordem.pausa ?? null;
  useEffect(() => {
    const id = window.setInterval(() => setAgora(Date.now()), pausa ? 250 : 1000);
    return () => window.clearInterval(id);
  }, [pausa]);
  const inicio = Date.parse(ordem.startedAt);
  const relogio = Number.isFinite(inicio) ? tempoDesde(inicio, agora).padStart(8, '00:') : '--:--';
  const manual = ordem.modo === 'manual';
  const abates = ordem.abates ?? {};
  const entrou = ordem.saque ?? {};
  const noCofre = ordem.cofre?.saque ?? {};
  const vendido = ordem.venda?.saque ?? {};
  // A mochila: o que entrou nesta caçada menos o que já foi para o cofre ou foi vendido.
  const naMochila = Object.entries(entrou)
    .map(([id, q]) => [id, q - (noCofre[id] ?? 0) - (vendido[id] ?? 0)] as const)
    .filter(([, q]) => q > 0);
  const itens = naMochila.reduce((s, [, q]) => s + q, 0);
  const peso = voce?.peso ?? 0;
  const max = voce?.pesoMax ?? 0;
  const ao = AO_VER[ordem.aoVerJogador] ?? AO_VER.batalhar;
  const leva = ordenar(hunt?.loot ?? []).filter(l => ordem.filtro.includes(l.defId));
  const aoEncher: HuntAoEncher = ordem.aoEncher ?? 'cofre';
  const cacada = t(`cacada.${ordem.huntId}.nome`);
  // Mais de quatro inimigos: rostos menores, em até duas linhas (cinco por linha cabem nos 290 px).
  const muitos = (hunt?.inimigos.length ?? 0) > MOBS_SEM_CARROSSEL;

  // A pausa: a fase de agora e quanto falta para voltar.
  const fases = pausa?.fases ?? [];
  const fase = fases.find(f => agora < f.ateMs) ?? fases[fases.length - 1];
  const fimPausa = fases.length ? fases[fases.length - 1].ateMs : 0;
  const inicioPausa = fases.length ? fases[0].ateMs - (pausa?.tipo === 'venda' ? regras.vendaS : regras.recallS) * 1000 : 0;
  const falta = Math.max(0, (fimPausa - agora) / 1000);
  const feito = fimPausa > inicioPausa ? Math.min(1, Math.max(0, (agora - inicioPausa) / (fimPausa - inicioPausa))) : 0;
  const pulso = pausa
    ? pausa.tipo === 'venda'
      ? { cls: ' is-venda', txt: t('cv.pulsoVendendo') }
      : { cls: ' is-viagem', txt: t(pausa.tipo === 'cofre' ? 'cv.pulsoCofre' : 'cv.pulsoEsvaziando') }
    : { cls: manual ? ' is-manual' : '', txt: t(manual ? 'cacar.modo.manual' : 'cacar.modo.automatico') };
  const cofre = ordem.cofre;
  const venda = ordem.venda;
  const trocarRegra = () => {
    if (!vip) { setExplica(e => !e); return; }
    onRegra(aoEncher === 'vender' ? 'cofre' : 'vender');
  };

  return (
    <>
      <div className="ch-live__topo">
        <span className={`ch-pulso${pulso.cls}`}><i />{pulso.txt}</span>
        <span className="ch-relogio" title={t('ch.tempo')}><IcRelogio /><b>{relogio}</b></span>
      </div>
      <h3 className="ch-live__n">{cacada}</h3>
      <div className={`ch-live__mobs${muitos ? ' is-muitos' : ''}`}>
        {(hunt?.inimigos ?? []).map(m => (
          <div key={m.defId} className="ch-abate" title={t('ch.mortos', { mob: nomeMob(m), n: abates[m.defId] ?? 0 })}>
            <FaceMob aparencia={aparenciaDe(m)} px={muitos ? 42 : 50} />
            <b><small>×</small>{numero(abates[m.defId] ?? 0)}</b>
          </div>
        ))}
      </div>
      <div className="ch-live__tiles">
        <div className="ch-tile" title={t('ch.prataDica')}><ImgAparada src={PILE_PRATA} /><b>{numero(ordem.prata ?? 0)}</b></div>
        <button type="button" className="ch-tile--btn" aria-expanded={aba} title={t('ch.saqueDica')} onClick={() => setAba(a => !a)}>
          <ImgAparada src="/sprites/itens/hud/bag.png" /><b>{numero(itens)}</b><i>›</i>
        </button>
      </div>
      <div className="cv-tiles">
        {(aoEncher === 'cofre' || (cofre?.viagens ?? 0) > 0) && (
          <div className="cv-tile" title={t('cv.noCofreDica')}>
            <IcCofre /><b>{numero(cofre?.itens ?? 0)}</b><span>{t('cv.noCofre')}</span>
            <em>{t((cofre?.viagens ?? 0) === 1 ? 'cv.viagem' : 'cv.viagens', { n: numero(cofre?.viagens ?? 0) })}</em>
          </div>
        )}
        {(aoEncher === 'vender' || (venda?.vendas ?? 0) > 0) && (
          <div className="cv-tile is-prata" title={t('cv.daVendaDica')}>
            <ImgAparada src={PILE_PRATA} /><b>{numero(venda?.prata ?? 0)}</b><span>{t('cv.daVenda')}</span>
            <em>{t('cv.itens', { n: numero(venda?.itens ?? 0) })}</em>
          </div>
        )}
      </div>
      {pausa && (
        <div className="cv-viagem" role="status">
          <span>{textoDaFase(fase?.codigo ?? '', regras.banco, cacada)}</span>
          {fases.length > 0 && <em>{t('cv.voltaEm', { s: segundos(falta) })}</em>}
          <div className="bar-mini"><i style={{ width: `${Math.round(feito * 100)}%` }} /></div>
        </div>
      )}
      {aba && (
        <aside className="gump ch-aba" aria-label={t('ch.saqueDaCacada')}>
          <h4 className="ch-sec">{t('ch.saqueDaCacada')}</h4>
          <div className="cv-aba__g">
            <AbaGrupo titulo={t('cv.naMochila')} itens={naMochila} />
            <AbaGrupo titulo={t('cv.noCofreAba')} itens={Object.entries(noCofre)} />
            <AbaGrupo titulo={t('cv.vendido')} itens={Object.entries(vendido)}
              extra={(venda?.prata ?? 0) > 0 ? `+${numero(venda?.prata ?? 0)}` : undefined} />
            {(venda?.vendas ?? 0) > 0 && (
              <p className="cv-recompra">{t('cv.recompra', { min: regras.recompraMin })}</p>
            )}
          </div>
        </aside>
      )}
      <div className={`ch-bolsa${pausa ? ' is-cheia' : ''}`} title={t('cacar.stones', { peso: numero(peso), max: numero(max) })}>
        <IcPeso />
        <div className="ch-bolsa__b"><i style={{ width: `${max ? Math.min(100, (peso / max) * 100) : 0}%` }} /></div>
        <small>{numero(Math.round(peso))}/{numero(Math.round(max))}</small>
      </div>
      <button type="button" className="cv-regra" onClick={trocarRegra}
        title={vip ? t('cv.regraTrocar') : t('cv.soVip')}>
        {aoEncher === 'vender' ? <ImgAparada src={PILE_PRATA} /> : <IcCofre />}
        {t('cv.mochilaCheia')}: <b>{t(aoEncher === 'vender' ? 'cv.regraVender' : 'cv.regraCofre')}</b>
        {aoEncher === 'vender' && <span>· &lt; {ordem.raroAbaixoDe ?? regras.raroPadrao}% {t('cv.noCofre')}</span>}
      </button>
      {explica && !vip && (
        <div className="cv-vip" role="status">
          <span><b>{t('cv.upsellTitulo')}</b> {t('cv.upsellTexto')}</span>
          <span>{t('cv.upsellSemVip')}</span>
          {onVerVip && <button type="button" className="btn btn--tiny btn--primary" onClick={onVerVip}>{t('cv.verVip')}</button>}
        </div>
      )}
      <div className="ch-live__leva" title={t('ch.coletando')}>
        {leva.map(l => <IconeItem key={l.defId} id={l.defId} />)}
        <ImgAparada src={ao.img} style={{ marginLeft: 6 }} title={t('ch.aoVerDica', { modo: t(ao.nome) })} />
      </div>
      {fantasma && <p className="ch-aviso">{t('cacar.avisoMorte')}</p>}
      <div className="ch-live__pe">
        {manual && <button type="button" className="btn btn--tiny" disabled={busy} onClick={onAuto}>{t('cacar.modo.automatico')}</button>}
        <button type="button" className="btn btn--ghost btn--tiny" disabled={busy} onClick={onVoltar}>{t('ch.voltar')}</button>
      </div>
    </>
  );
}

/** Um destino do saque na aba: o título (com a prata da venda, se houver) e a grade, ou "nada". */
function AbaGrupo({ titulo, itens, extra }: { titulo: string; itens: readonly (readonly [string, number])[]; extra?: string }) {
  const vivos = itens.filter(([, q]) => q > 0);
  return (
    <>
      <h5>{titulo}{extra && <em>{extra}</em>}</h5>
      {vivos.length
        ? <div className="ch-aba__g">{vivos.map(([id, q]) => <span key={id} className="ch-q" title={nomeDoItem(id)}><IconeItem id={id} /><b>{numero(q)}</b></span>)}</div>
        : <p className="ch-aba__vazio">{t('ch.nadaAinda')}</p>}
    </>
  );
}

/** "Enquanto você esteve fora" (techspec-cacada-venda §06): o tempo resolvido no login, até o OK. */
function CartaoFora({ fora, hunt, busy, onOk }: { fora: HuntFora; hunt: HuntView | null; busy: boolean; onOk(): void }) {
  const cacada = t(`cacada.${fora.huntId}.nome`);
  const fim = fora.fim === 'morte'
    ? t('cv.foraMorte', { cacada })
    : fora.fim === 'cofre'
      ? t('cv.foraCofre', { cidade: fora.cidade })
      : fora.fim === 'tempo'
        ? t('cv.foraTempo', { cidade: fora.cidade })
        : t('cv.foraSegue');
  const rosto = hunt?.inimigos[0];
  return (
    <div className="cv-fora" role="status">
      <h4>{t('cv.foraTitulo')} <em>{duracao(fora.segundos)}</em></h4>
      <div className="cv-fora__g">
        <span>{rosto && <FaceMob aparencia={aparenciaDe(rosto)} px={18} />}<b>{numero(fora.mortos)}</b> {t('cv.foraMortos')}</span>
        <span><ImgAparada src={PILE_PRATA} /><b>{numero(fora.prata)}</b> {t('cv.foraNoChao')}</span>
        {fora.viagens > 0 && (
          <span><IcCofre /><b>{numero(fora.guardados)}</b> {t('cv.foraNoCofre', { n: numero(fora.viagens) })}</span>
        )}
        {fora.vendas > 0 && (
          <span><ImgAparada src={PILE_PRATA} /><b>{numero(fora.prataDaVenda)}</b> {t('cv.foraDaVenda', { n: numero(fora.vendidos) })}</span>
        )}
      </div>
      {fora.vipVenceu && <p className="cv-fora__aviso">{t('cv.foraVipVenceu')}</p>}
      <p className={`cv-fora__fim${fora.fim ? ' is-fim' : ''}`}>{fim}</p>
      <p className="cv-fora__nota">{t('cv.foraSemPvp')}</p>
      <button type="button" className="btn btn--tiny" disabled={busy} onClick={onOk}>OK</button>
    </div>
  );
}

/**
 * Os inimigos da ficha. Com mais de quatro, duas linhas que correm para o lado: o jogador puxa (toque ou
 * arraste do mouse) ou usa as setas; as bolinhas dizem em que página está (techspec-ice-dungeon §11).
 */
function MobsDaFicha({ inimigos }: { inimigos: readonly HuntMob[] }) {
  const car = useCarrossel();
  const carrossel = inimigos.length > MOBS_SEM_CARROSSEL;
  const controles = carrossel && car.paginas > 1;
  return (
    <>
      <h4 className="ch-sec">
        {t('ch.inimigos')}
        {controles && (
          <span className="ch-sec__dir ch-car__nav">
            <button type="button" className="ch-mini" disabled={!car.podeVoltar} aria-label={t('ch.mobsAntes')} title={t('ch.mobsAntes')} onClick={() => car.passo(-1)}>‹</button>
            <span className="ch-car__pg" aria-live="polite">{car.pagina + 1}/{car.paginas}</span>
            <button type="button" className="ch-mini" disabled={!car.podeAvancar} aria-label={t('ch.mobsDepois')} title={t('ch.mobsDepois')} onClick={() => car.passo(1)}>›</button>
          </span>
        )}
      </h4>
      <div
        ref={car.ref}
        className={`ch-mobs${carrossel ? ' is-carrossel' : ''}${car.arrastando ? ' is-arrastando' : ''}`}
        onPointerDown={carrossel ? car.onPointerDown : undefined}
      >
        {inimigos.map(m => <CartaoMob key={m.defId} m={m} />)}
      </div>
      {controles && (
        <div className="ch-car__dots" role="tablist" aria-label={t('ch.inimigos')}>
          {Array.from({ length: car.paginas }, (_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === car.pagina}
              aria-label={`${i + 1}/${car.paginas}`}
              className={i === car.pagina ? 'is-on' : ''}
              onClick={() => car.irPara(i)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function CartaoMob({ m }: { m: HuntMob }) {
  const r = m.resumo;
  return (
    <div className="ch-mob">
      <div className="ch-mob__img">
        <RetratoAnimado aparencia={aparenciaDe(m)} alt={nomeMob(m)} />
        <span className="ch-mob__q">×{m.quantidade}</span>
      </div>
      <div className="ch-mob__d">
        <div className="ch-mob__t">
          <span className="ch-mob__n" title={nomeMob(m)}>{nomeMob(m)}</span>
          <span className="ch-imune">
            <span className={r.poisonImmune ? 'is-imune' : ''} title={t(r.poisonImmune ? 'ch.imuneVeneno' : 'ch.venenoOk')}><IcVeneno /></span>
            <span className={r.permiteParalisia === false ? 'is-imune' : ''} title={t(r.permiteParalisia === false ? 'ch.imuneParalisia' : 'ch.paralisiaOk')}><IcParalisa /></span>
          </span>
        </div>
        <div className="ch-stats">
          <span className="ch-st" title={t('ch.vida')}><IcVida />{numero(r.hits)}</span>
          <span className="ch-st" title={t('ch.dano')}><IcDano />{r.danoMax ? `${r.danoMin}–${r.danoMax}` : '?'}</span>
          <span className="ch-st" title={t('ch.ar')}><IcAr />{numero(r.ar)}</span>
          <span className="ch-st" title={t('ch.rm')}><IcRm />{numero(r.magicResist)}</span>
        </div>
      </div>
    </div>
  );
}
